/* ==========================================================================
   Elite Fishin — global behaviour
   Scroll reveal, drawers, quantity steppers, sticky header.
   Written as small custom elements so sections opt in by markup alone.
   ========================================================================== */

(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     Scroll reveal
     --------------------------------------------------------------------- */

  function initReveal() {
    var targets = document.querySelectorAll('.ef-reveal:not(.is-revealed)');
    if (!targets.length) return;

    // No IntersectionObserver, or motion is unwelcome: show everything at once.
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  // Index children of a reveal group so CSS can stagger them
  function indexRevealGroups() {
    document.querySelectorAll('.ef-reveal-group').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child, i) {
        child.style.setProperty('--ef-reveal-index', i);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Focus trapping — shared by every drawer
     --------------------------------------------------------------------- */

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var nodes = container.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------
     <ef-drawer> — generic slide-out panel
     Markup: <ef-drawer id="cart-drawer"> … </ef-drawer>
     Trigger: <button data-ef-drawer-open="cart-drawer">
     --------------------------------------------------------------------- */

  var EfDrawer = (function () {
    function Drawer() {}

    Drawer.prototype = Object.create(HTMLElement.prototype);

    return Drawer;
  })();

  if ('customElements' in window) {
    customElements.define(
      'ef-drawer',
      class extends HTMLElement {
        connectedCallback() {
          this.panel = this.querySelector('.ef-drawer');
          this.overlay = this.querySelector('.ef-overlay');
          this.lastFocused = null;

          this.onKeydown = this.onKeydown.bind(this);

          this.querySelectorAll('[data-ef-drawer-close]').forEach((btn) => {
            btn.addEventListener('click', () => this.close());
          });

          if (this.overlay) {
            this.overlay.addEventListener('click', () => this.close());
          }
        }

        open(opener) {
          this.lastFocused = opener || document.activeElement;
          this.setAttribute('open', '');
          if (this.panel) this.panel.classList.add('is-open');
          if (this.overlay) this.overlay.classList.add('is-open');
          document.body.classList.add('ef-scroll-locked');
          document.addEventListener('keydown', this.onKeydown);

          // Move focus into the panel on the next frame, once it is visible
          window.requestAnimationFrame(() => {
            var target = this.querySelector('[data-ef-drawer-focus]') || this.querySelector(FOCUSABLE);
            if (target) target.focus();
          });
        }

        close() {
          this.removeAttribute('open');
          if (this.panel) this.panel.classList.remove('is-open');
          if (this.overlay) this.overlay.classList.remove('is-open');
          document.body.classList.remove('ef-scroll-locked');
          document.removeEventListener('keydown', this.onKeydown);

          if (this.lastFocused) this.lastFocused.focus();
        }

        onKeydown(event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }
          if (event.key === 'Tab') {
            trapFocus(this, event);
          }
        }
      }
    );
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-ef-drawer-open]');
    if (!opener) return;

    var drawer = document.getElementById(opener.getAttribute('data-ef-drawer-open'));
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    drawer.open(opener);
  });

  /* ---------------------------------------------------------------------
     Mega menu — hovering or focusing a category in the left rail swaps the
     right-hand pane. Every rail item is a real link, so this only ever
     changes what is previewed; it never swallows the click.
     --------------------------------------------------------------------- */

  function activateMegaCategory(trigger) {
    var mega = trigger.closest('[data-ef-mega]');
    if (!mega) return;

    var key = trigger.getAttribute('data-ef-mega-cat');

    mega.querySelectorAll('[data-ef-mega-cat]').forEach(function (cat) {
      cat.classList.toggle('is-active', cat === trigger);
    });

    mega.querySelectorAll('[data-ef-mega-pane]').forEach(function (pane) {
      pane.hidden = pane.getAttribute('data-ef-mega-pane') !== key;
    });
  }

  // pointerover/focusin bubble, so one listener covers every rail item
  document.addEventListener('pointerover', function (event) {
    var trigger = event.target.closest('[data-ef-mega-cat]');
    if (trigger) activateMegaCategory(trigger);
  });

  document.addEventListener('focusin', function (event) {
    var trigger = event.target.closest('[data-ef-mega-cat]');
    if (trigger) activateMegaCategory(trigger);
  });

  // Reset each panel to its first category once the menu closes
  document.addEventListener('pointerleave', function (event) {
    var item = event.target.closest ? event.target.closest('.ef-nav__item') : null;
    if (!item) return;

    var first = item.querySelector('[data-ef-mega-cat]');
    if (first) activateMegaCategory(first);
  }, true);

  /* ---------------------------------------------------------------------
     Auto-submitting forms (the collection sort dropdown)
     The form still has a submit button inside <noscript>, so choosing a sort
     order works with JS off too.
     --------------------------------------------------------------------- */

  document.addEventListener('change', function (event) {
    var form = event.target.closest('form[data-ef-autosubmit]');
    if (form) form.submit();
  });

  /* ---------------------------------------------------------------------
     Quantity stepper
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-ef-qty]');
    if (!button) return;

    var wrapper = button.closest('.ef-qty');
    var input = wrapper && wrapper.querySelector('input');
    if (!input) return;

    var step = button.getAttribute('data-ef-qty') === 'up' ? 1 : -1;
    var min = parseInt(input.getAttribute('min') || '0', 10);
    var next = (parseInt(input.value, 10) || min) + step;

    // `max` is only present when Shopify tracks the variant and refuses
    // oversell. Without this the plus button climbs past stock that cannot be
    // fulfilled, and the shopper only finds out when the cart rejects it.
    var maxAttr = input.getAttribute('max');
    if (maxAttr !== null && maxAttr !== '') {
      var max = parseInt(maxAttr, 10);
      if (!isNaN(max)) next = Math.min(next, max);
    }

    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------------
     Sticky / shrinking header
     --------------------------------------------------------------------- */

  function initHeader() {
    var header = document.querySelector('[data-ef-header]');
    if (!header || header.getAttribute('data-ef-header-shrink') !== 'true') return;

    var ticking = false;

    function update() {
      header.classList.toggle('is-shrunk', window.scrollY > 80);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ---------------------------------------------------------------------
     Boot — and re-boot when the theme editor swaps a section
     --------------------------------------------------------------------- */

  function init() {
    indexRevealGroups();
    initReveal();
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
